/**
 * Selectors / pure functions dla Analytics.
 * Port z js/analytics.js — kalkulacje 1:1, czyste funkcje.
 */
import type { Transaction } from '../transactions/transactionsStore';
import type { Category } from '../categories/categoriesStore';

export type RangeKey = '1d' | '7d' | '1m' | '3m' | '6m' | '1y' | 'all';

export const RANGE_LABELS: Record<RangeKey, string> = {
  '1d': '1d',
  '7d': '7d',
  '1m': '1m',
  '3m': '3m',
  '6m': '6m',
  '1y': '1r',
  all: 'Wszystko',
};

export function getRangeBounds(range: RangeKey, now = new Date()): { start: Date; end: Date } {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  switch (range) {
    case '1d':
      break;
    case '7d':
      start.setDate(start.getDate() - 6);
      break;
    case '1m':
      start.setMonth(start.getMonth() - 1);
      break;
    case '3m':
      start.setMonth(start.getMonth() - 3);
      break;
    case '6m':
      start.setMonth(start.getMonth() - 6);
      break;
    case '1y':
      start.setFullYear(start.getFullYear() - 1);
      break;
    case 'all':
      start.setFullYear(2000, 0, 1);
      break;
  }
  return { start, end };
}

/** Filtruj transakcje po wallet (lub null = cała rodzina) i zakresie. */
export function filterTxByRange(
  txs: Transaction[],
  walletId: string | null,
  range: RangeKey,
  now = new Date(),
): Transaction[] {
  const { start, end } = getRangeBounds(range, now);
  return txs.filter((t) => {
    if (t.date < start || t.date > end) return false;
    if (!walletId) return true;
    return t.walletId === walletId || t.toWalletId === walletId;
  });
}

export interface CategoryBreakdownRow {
  categoryId: string;
  name: string;
  icon: string;
  color: string;
  sum: number; // grosze
  pct: number; // 0-100
}

export function categoryBreakdown(
  txs: Transaction[],
  categories: Category[],
  mode: 'income' | 'expense',
): { rows: CategoryBreakdownRow[]; total: number } {
  const map = new Map<string, number>();
  for (const t of txs) {
    if (t.type !== mode) continue;
    const cid = t.categoryId ?? 'unknown';
    map.set(cid, (map.get(cid) ?? 0) + t.amount);
  }
  const total = [...map.values()].reduce((s, v) => s + v, 0);
  const rows: CategoryBreakdownRow[] = [...map.entries()]
    .map(([cid, sum]) => {
      const c = categories.find((x) => x.id === cid);
      return {
        categoryId: cid,
        name: c?.name ?? 'Inne',
        icon: c?.icon ?? 'category',
        color: c?.color ?? '#9E9E9E',
        sum,
        pct: total > 0 ? Math.round((sum / total) * 100) : 0,
      };
    })
    .sort((a, b) => b.sum - a.sum);
  return { rows, total };
}

/** Daily saldo w zakresie (dla line chart). aggregateAll = portfel Wspólny. */
export function dailyBalance(
  txs: Transaction[],
  walletId: string | null,
  range: RangeKey,
  now = new Date(),
): { date: Date; balance: number; income: number; expense: number }[] {
  const { start, end } = getRangeBounds(range, now);
  const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (24 * 3600 * 1000)));
  const points: { date: Date; balance: number; income: number; expense: number }[] = [];
  // Używamy do 90 punktów (dla long ranges grupujemy po tygodniach)
  const step = days <= 31 ? 1 : days <= 200 ? 7 : 30;
  for (let i = 0; i < days; i += step) {
    const dayEnd = new Date(start);
    dayEnd.setDate(start.getDate() + Math.min(i + step - 1, days - 1));
    dayEnd.setHours(23, 59, 59, 999);
    let balance = 0;
    let income = 0;
    let expense = 0;
    for (const t of txs) {
      if (t.date.getTime() > dayEnd.getTime()) continue;
      if (walletId) {
        if (t.type === 'income' && t.walletId === walletId) {
          balance += t.amount;
          if (t.date.getTime() >= start.getTime()) income += t.amount;
        } else if (t.type === 'expense' && t.walletId === walletId) {
          balance -= t.amount;
          if (t.date.getTime() >= start.getTime()) expense += t.amount;
        } else if (t.type === 'transfer') {
          if (t.walletId === walletId) balance -= t.amount;
          else if (t.toWalletId === walletId) balance += t.amount;
        }
      } else {
        if (t.type === 'income') {
          balance += t.amount;
          if (t.date.getTime() >= start.getTime()) income += t.amount;
        } else if (t.type === 'expense') {
          balance -= t.amount;
          if (t.date.getTime() >= start.getTime()) expense += t.amount;
        }
      }
    }
    points.push({ date: new Date(dayEnd), balance, income, expense });
  }
  return points;
}

export function savingsRate(income: number, expense: number): number {
  if (income <= 0) return 0;
  return Math.max(0, Math.round(((income - expense) / income) * 100));
}

export interface Subscription {
  tx: Transaction;
  category?: Category;
  isPaused: boolean;
}

export function listSubscriptions(txs: Transaction[], categories: Category[]): Subscription[] {
  const seen = new Map<string, Transaction>();
  for (const t of txs) {
    if (!t.isRecurring) continue;
    // unique by (categoryId + note + amount) — najnowszy egzemplarz
    const key = `${t.categoryId}|${t.note}|${t.amount}`;
    const prev = seen.get(key);
    if (!prev || t.date > prev.date) seen.set(key, t);
  }
  return [...seen.values()]
    .sort((a, b) => b.amount - a.amount)
    .map((tx) => ({
      tx,
      category: categories.find((c) => c.id === tx.categoryId),
      isPaused: tx.isPaused === true,
    }));
}
